<!--
  VoucherPurchaseTab — 바우처관리 › 충전·내역
  · 구매(충전) 요청 목록(GET /purchases) + 요청(PurchaseRequestModal) + 철회(POST /purchases/{idx}/cancel)
  · 바우처 원장(GET /ledger — 페이지 봉투 { items, total, page, size })
-->
<template>
  <div>
    <a-card
      title="바우처 구매(충전) 요청"
      sub="요청만으로 잔액이 오르지 않습니다 — 관리자가 입금을 확인해 확정하면 실입금액이 그대로 적립됩니다"
      overflow="visible"
    >
      <template #action>
        <div class="p-row-actions">
          <select v-model="purchaseStatus" class="p-input p-select p-select--sm" @change="loadPurchases">
            <option value="">전체 상태</option>
            <option value="REQUESTED">요청</option>
            <option value="CONFIRMED">확정</option>
            <option value="CANCELLED">철회</option>
            <option value="REJECTED">반려</option>
            <option value="VOIDED">확정 취소</option>
          </select>
          <a-btn size="sm" kind="primary" @click="showRequest = true">구매 요청</a-btn>
        </div>
      </template>
      <div v-if="loadingPurchases" class="p-loader"><div class="p-spinner" /></div>
      <a-empty v-else-if="!purchases.length" title="구매 요청이 없습니다" sub="‘구매 요청’ 으로 충전을 요청하고 안내된 참조번호로 입금하세요." />
      <a-table v-else :columns="purchaseCols" :rows="purchases" row-key="idx">
        <template #cell-reference="{ row }">
          <div class="p-cell-title p-mono">{{ row.reference || `VP-${row.idx}` }}</div>
          <div v-if="row.memo" class="p-cell-sub">{{ row.memo }}</div>
        </template>
        <template #cell-requested_amount="{ row }">
          <span class="p-amount">{{ fmtAmount(row.requested_amount, row.requested_currency) }}</span>
        </template>
        <template #cell-status="{ row }">
          <a-pill :kind="purchaseStatusKind(row.status)">{{ purchaseStatusLabel(row.status) }}</a-pill>
          <div v-if="row.cancel_reason" class="p-cell-sub">{{ row.cancel_reason }}</div>
        </template>
        <template #cell-credited="{ row }">
          <template v-if="row.credited_amount != null">
            <div class="p-amount p-amount--pos">{{ fmtAmount(row.credited_amount, row.voucher_currency) }}</div>
            <div v-if="row.paid_amount != null && (row.paid_currency !== row.voucher_currency)" class="p-cell-sub">입금 {{ fmtAmount(row.paid_amount, row.paid_currency) }}</div>
            <div v-if="row.payment_ref" class="p-cell-sub p-mono">{{ row.payment_ref }}</div>
          </template>
          <span v-else class="p-cell-sub">-</span>
        </template>
        <template #cell-created_at="{ row }">
          <span class="p-mono">{{ fmtDateTime(row.created_at) }}</span>
          <div v-if="row.requested_by_name" class="p-cell-sub">{{ row.requested_by_name }}</div>
        </template>
        <template #cell-confirmed_at="{ row }">
          <span class="p-mono">{{ row.confirmed_at ? fmtDateTime(row.confirmed_at) : '-' }}</span>
          <div v-if="row.deposited_on" class="p-cell-sub">입금일 {{ fmtDate(row.deposited_on) }}</div>
        </template>
        <template #cell-actions="{ row }">
          <a-btn
            v-if="row.status === 'REQUESTED'"
            size="sm"
            kind="danger"
            :disabled="cancelling === row.idx"
            @click="cancelPurchase(row)"
          >철회</a-btn>
        </template>
      </a-table>
    </a-card>

    <a-card title="바우처 내역(원장)" :sub="`총 ${ledger.total}건 · 금액은 부호대로, 오른쪽은 처리 후 잔액`" class="p-mt-16" overflow="visible">
      <div class="p-filters">
        <select v-model="ledgerFilters.entry_type" class="p-input p-select">
          <option value="">전체 구분</option>
          <option v-for="t in ledgerTypes" :key="t" :value="t">{{ ledgerTypeLabel(t) }}</option>
        </select>
        <span class="p-filters__label">기간</span>
        <input v-model="ledgerFilters.date_from" type="date" class="p-input" />
        <span class="p-filters__label">~</span>
        <input v-model="ledgerFilters.date_to" type="date" class="p-input" />
        <a-btn kind="primary" size="sm" :disabled="loadingLedger" @click="searchLedger">조회</a-btn>
        <a-btn kind="ghost" size="sm" @click="resetLedger">초기화</a-btn>
      </div>
      <div v-if="loadingLedger" class="p-loader"><div class="p-spinner" /></div>
      <a-empty v-else-if="!ledger.items.length" title="내역이 없습니다" />
      <template v-else>
        <a-table :columns="ledgerCols" :rows="ledger.items" row-key="idx">
          <template #cell-created_at="{ value }"><span class="p-mono">{{ fmtDateTime(value) }}</span></template>
          <template #cell-entry_type="{ value }"><a-pill :kind="ledgerTypeKind(value)">{{ ledgerTypeLabel(value) }}</a-pill></template>
          <template #cell-desc="{ row }">
            <template v-if="row.entry_type === 'REGISTER' || row.entry_type === 'REGISTER_REVERSAL'">
              <div class="p-cell-title">{{ row.student_name || '-' }} · {{ row.textbook_title || row.barcode || '-' }}</div>
              <div class="p-cell-sub">{{ row.classroom_name || '' }}<template v-if="row.voucher_idx"> · 바우처 #{{ row.voucher_idx }}</template></div>
            </template>
            <template v-else-if="row.entry_type === 'PURCHASE' || row.entry_type === 'PURCHASE_VOID'">
              <div class="p-cell-title">{{ row.purchase_idx ? `VP-${row.purchase_idx}` : '충전' }}</div>
              <div v-if="row.paid_amount != null" class="p-cell-sub">입금 {{ fmtAmount(row.paid_amount, row.paid_currency) }}</div>
            </template>
            <template v-else>
              <div class="p-cell-title">{{ row.memo || '-' }}</div>
            </template>
          </template>
          <template #cell-actor="{ row }">
            <span class="p-cell-sub">{{ row.actor_name || row.actor_emp_name || '-' }}</span>
          </template>
          <template #cell-amount="{ row }">
            <span class="p-amount" :class="isNegativeAmount(row.amount) ? 'p-amount--neg' : 'p-amount--pos'">{{ fmtSignedAmount(row.amount) }}</span>
          </template>
          <template #cell-balance_after="{ row }"><span class="p-mono">{{ fmtAmount(row.balance_after, row.currency_code) }}</span></template>
        </a-table>
        <div v-if="ledgerTotalPages > 1" class="p-pagination">
          <pagination :current-page="ledger.page" :total-pages="ledgerTotalPages" @update:current-page="goLedgerPage" />
        </div>
      </template>
    </a-card>

    <purchase-request-modal
      v-if="showRequest"
      :org-idx="orgIdx"
      :organization="organization"
      @close="showRequest = false"
      @requested="onRequested"
    />
  </div>
</template>

<script>
import ACard from '@/components/Pineapple/ACard.vue';
import ATable from '@/components/Pineapple/ATable.vue';
import APill from '@/components/Pineapple/APill.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';
import Pagination from '@/components/Pagination.vue';
import PurchaseRequestModal from './PurchaseRequestModal.vue';
import {
  fmtAmount, fmtSignedAmount, fmtDate, fmtDateTime, isNegativeAmount,
  purchaseStatusKind, purchaseStatusLabel, LEDGER_TYPES, ledgerTypeKind, ledgerTypeLabel, voucherErrorText,
} from '../orgUtils';

const LEDGER_SIZE = 50;

export default {
  name: 'VoucherPurchaseTab',
  components: { ACard, ATable, APill, ABtn, AEmpty, Pagination, PurchaseRequestModal },
  props: {
    orgIdx: { type: [Number, String], required: true },
    organization: { type: Object, default: null },
  },
  data() {
    return {
      purchases: [],
      purchaseStatus: '',
      loadingPurchases: false,
      cancelling: null,
      showRequest: false,
      ledger: { items: [], total: 0, page: 1, size: LEDGER_SIZE },
      ledgerFilters: { entry_type: '', date_from: '', date_to: '' },
      loadingLedger: false,
      ledgerTypes: LEDGER_TYPES,
      purchaseCols: [
        { key: 'reference',        label: '참조번호' },
        { key: 'requested_amount', label: '요청 금액',  width: '140px', align: 'right' },
        { key: 'status',           label: '상태',       width: '110px' },
        { key: 'credited',         label: '적립(확정)', width: '160px', align: 'right' },
        { key: 'created_at',       label: '요청일',     width: '200px' },
        { key: 'confirmed_at',     label: '확정일',     width: '200px' },
        { key: 'actions',          label: '',           width: '80px',  align: 'right' },
      ],
      ledgerCols: [
        { key: 'created_at',    label: '일시',   width: '200px' },
        { key: 'entry_type',    label: '구분',   width: '100px' },
        { key: 'desc',          label: '내용' },
        { key: 'actor',         label: '처리',   width: '110px' },
        { key: 'amount',        label: '금액',   width: '130px', align: 'right' },
        { key: 'balance_after', label: '잔액',   width: '150px', align: 'right' },
      ],
    };
  },
  computed: {
    ledgerTotalPages() {
      return Math.max(1, Math.ceil((this.ledger.total || 0) / (this.ledger.size || LEDGER_SIZE)));
    },
  },
  mounted() {
    this.loadPurchases();
    this.loadLedger(1);
  },
  methods: {
    fmtAmount, fmtSignedAmount, fmtDate, fmtDateTime, isNegativeAmount,
    purchaseStatusKind, purchaseStatusLabel, ledgerTypeKind, ledgerTypeLabel,
    async loadPurchases() {
      this.loadingPurchases = true;
      try {
        const params = {};
        if (this.purchaseStatus) params.status = this.purchaseStatus;
        const res = await this.$axios({ method: 'get', url: `/voucher/org/${this.orgIdx}/purchases`, params, loadingBar: false });
        this.purchases = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.purchases = [];
        this.$notifyVue({ type: 'error', title: voucherErrorText(e, '구매 요청 내역을 불러오지 못했습니다.') });
      } finally {
        this.loadingPurchases = false;
      }
    },
    async loadLedger(page) {
      this.loadingLedger = true;
      try {
        const params = { page, size: LEDGER_SIZE };
        if (this.ledgerFilters.entry_type) params.entry_type = this.ledgerFilters.entry_type;
        if (this.ledgerFilters.date_from) params.date_from = this.ledgerFilters.date_from;
        if (this.ledgerFilters.date_to) params.date_to = this.ledgerFilters.date_to;
        const res = await this.$axios({ method: 'get', url: `/voucher/org/${this.orgIdx}/ledger`, params, loadingBar: false });
        const d = res.data || {};
        this.ledger = {
          items: Array.isArray(d.items) ? d.items : [],
          total: Number(d.total) || 0,
          page: Number(d.page) || page,
          size: Number(d.size) || LEDGER_SIZE,
        };
      } catch (e) {
        this.ledger = { items: [], total: 0, page: 1, size: LEDGER_SIZE };
        this.$notifyVue({ type: 'error', title: voucherErrorText(e, '바우처 내역을 불러오지 못했습니다.') });
      } finally {
        this.loadingLedger = false;
      }
    },
    searchLedger() { this.loadLedger(1); },
    resetLedger() {
      this.ledgerFilters = { entry_type: '', date_from: '', date_to: '' };
      this.loadLedger(1);
    },
    goLedgerPage(p) { this.loadLedger(p); },
    onRequested() {
      this.loadPurchases();
      this.$store.dispatch('organization/refresh');
    },
    async cancelPurchase(row) {
      const confirmation = await this.$swal({
        title: '구매 요청 철회',
        text: `${row.reference || `VP-${row.idx}`} (${fmtAmount(row.requested_amount, row.requested_currency)}) 요청을 철회하시겠습니까?`,
        icon: 'warning',
        showConfirmButton: true, showCancelButton: true,
        confirmButtonText: '철회', cancelButtonText: '취소',
        confirmButtonColor: '#C44A3A',
      });
      if (!confirmation.isConfirmed) return;
      this.cancelling = row.idx;
      try {
        await this.$axios({ method: 'post', url: `/voucher/org/${this.orgIdx}/purchases/${row.idx}/cancel`, loadingBar: true });
        this.$notifyVue({ type: 'success', title: '구매 요청을 철회했습니다.' });
        this.loadPurchases();
      } catch (e) {
        this.$notifyVue({ type: 'error', title: voucherErrorText(e, '구매 요청을 철회하지 못했습니다.') });
      } finally {
        this.cancelling = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../_org.scss';

.p-select--sm { width: auto; min-width: 120px; padding-top: 6px; padding-bottom: 6px; font-size: 12px; }
</style>
